import React from 'react';
import { Button, Col, Clickable, Input, Row, Spinner2, TabControl, Text } from 'components';
import { Avatar } from 'components/Tailwindcss';
import { theme } from 'lib';
import { DetailSection, PredictionRow, StatusBadge } from '../styles';

// Width guard only — see sanitizeScore. Counts the minus sign, so the widest
// values a score field can hold are 9999 and -999.
const MAX_SCORE_INPUT_CHARS = 4;

const PredictionsSection = ({
  game,
  predictions,
  predictionGroups,
  loadingPredictions,
  isEditing,
  editTemplates,
  setEditTemplates,
  predictionLimits = { minItems: 6, maxItems: 8, minScore: 0, maxScore: 20 },
  predictionVoteCounts = { half_time: 0, full_time: 0, total: 0 },
  refreshingPredictions,
  onRefreshPredictions,
  selectedPredictionType,
  setSelectedPredictionType,
  canEditGame,
  onStartEdit,
  onCancelEdit,
  onSaveEdit,
  onViewVotes,
  onViewComments,
  onToggleActive
}) => {
  const { minItems, maxItems, minScore, maxScore } = predictionLimits;
  const atMin = editTemplates.length <= minItems;
  const atMax = editTemplates.length >= maxItems;

  // Keeps the field to something the row can actually render — a pasted 20-digit
  // number otherwise overflows the layout. This is deliberately NOT a range
  // check: out-of-range scores stay typable so the create/update endpoints get to
  // reject them and the admin sees the real reason.
  //
  // It has to happen in JS: the shared Input always renders type="text"
  // regardless of the type prop, so the browser's own number handling and any
  // min/max attributes never apply.
  const sanitizeScore = (raw) => {
    const prefix = String(raw).trim().startsWith('-') ? '-' : '';
    const digits = String(raw).replace(/\D/g, '');

    return prefix + digits.slice(0, MAX_SCORE_INPUT_CHARS - prefix.length);
  };

  return (
    <>
      <Text h4 bold marg="24px 0 16px">
        Predictions
      </Text>

      <DetailSection>
        {!loadingPredictions && (
          <Row center start gap="8px" marg="0 0 12px">
            <Text b1 bold>
              {predictionVoteCounts.total}
            </Text>
            <Text c1 col={theme.GREY_60}>
              total predictions · {predictionVoteCounts.half_time} half time ·{' '}
              {predictionVoteCounts.full_time} full time
            </Text>
          </Row>
        )}

        <TabControl
          options={[{ title: 'Half Time' }, { title: 'Full Time' }]}
          selectedIdx={selectedPredictionType}
          handleSelect={setSelectedPredictionType}
          marg="0 0 16px"
        />

        <Row wid="100%" justifyContent="space-between" centerIn marg="0 0 16px">
          <Row centerIn gap="12px">
            {(() => {
              const currentGroup =
                selectedPredictionType === 0
                  ? predictionGroups?.half_time
                  : predictionGroups?.full_time;
              if (!currentGroup) return null;

              return (
                <>
                  <StatusBadge status={currentGroup.isActive ? 'in-play' : 'hidden'}>
                    {currentGroup.isActive ? 'Active' : 'Hidden'}
                  </StatusBadge>
                  <Button
                    text={currentGroup.isActive ? 'Hide' : 'Activate'}
                    onClick={() => onToggleActive(currentGroup._id)}
                    bg={currentGroup.isActive ? theme.GREY_40 : theme.GREEN}
                    xs
                    wid="auto"
                  />
                </>
              );
            })()}
          </Row>

          <Row justifyContent="flex-end" gap="8px">
            <Button
              icon="refresh"
              iconSize={22}
              loading={refreshingPredictions}
              disabled={isEditing}
              onClick={onRefreshPredictions}
              sm
              outlined
              wid="auto"
            />
            {canEditGame && !isEditing && (
              <Button
                text={
                  (selectedPredictionType === 0 ? predictions.half_time : predictions.full_time)
                    ?.length > 0
                    ? 'Edit'
                    : 'Add'
                }
                icon={
                  (selectedPredictionType === 0 ? predictions.half_time : predictions.full_time)
                    ?.length > 0
                    ? 'edit'
                    : 'add'
                }
                onClick={onStartEdit}
                sm
                bg={theme.SECONDARY}
                wid="auto"
              />
            )}
            {isEditing && (
              <Row gap="8px" justifyContent="flex-end">
                <Button text="Cancel" onClick={onCancelEdit} sm bg={theme.GREY_40} wid="auto" />
                <Button text="Save" onClick={onSaveEdit} sm bg={theme.PRIMARY} wid="auto" />
              </Row>
            )}
          </Row>
        </Row>

        {loadingPredictions ? (
          <Col center pad="40px 0">
            <Spinner2 />
          </Col>
        ) : isEditing ? (
          <Col gap="12px" pad="16px">
            <Col gap="4px">
              <Text c1 col={theme.GREY_60}>
                Options can be edited or removed until fans engage with them. Once an option has
                votes or comments its score is locked.
              </Text>
              <Text c1 bold col={atMax ? theme.SECONDARY : theme.GREY_60}>
                {editTemplates.length} / {maxItems} options used (min {minItems}, max {maxItems})
              </Text>
              <Text c1 col={theme.GREY_60}>
                Scores must be whole numbers between {minScore} and {maxScore}.
              </Text>
            </Col>
            {editTemplates.map((template, idx) => {
              // Votes or comments freeze an option, matching the rule the update
              // endpoint enforces.
              const voteCount = template.votesCount || 0;
              const commentCount = template.commentsCount || 0;
              const isLocked = voteCount > 0 || commentCount > 0;
              const engagement = [
                voteCount > 0 ? 'votes' : null,
                commentCount > 0 ? 'comments' : null
              ]
                .filter(Boolean)
                .join(' and ');
              const cannotRemove = isLocked || atMin;
              return (
                <Col key={template.key} noFlex gap="4px">
                  <Row centerAll gap="12px">
                    <Text b2>Home:</Text>
                    <Input
                      type="number"
                      value={template.home}
                      disabled={isLocked}
                      bg={isLocked ? theme.GREY_10 : undefined}
                      onChange={(e) => {
                        if (isLocked) return;
                        const newTemplates = [...editTemplates];
                        newTemplates[idx].home = sanitizeScore(e.target.value);
                        setEditTemplates(newTemplates);
                      }}
                      wid="80px"
                    />
                    <Text b2>Away:</Text>
                    <Input
                      type="number"
                      value={template.away}
                      disabled={isLocked}
                      bg={isLocked ? theme.GREY_10 : undefined}
                      onChange={(e) => {
                        if (isLocked) return;
                        const newTemplates = [...editTemplates];
                        newTemplates[idx].away = sanitizeScore(e.target.value);
                        setEditTemplates(newTemplates);
                      }}
                      wid="80px"
                    />
                    <Button
                      text="Remove"
                      disabled={cannotRemove}
                      onClick={() => {
                        if (cannotRemove) return;
                        const newTemplates = [...editTemplates];
                        newTemplates.splice(idx, 1);
                        setEditTemplates(newTemplates);
                      }}
                      bg={theme.RED}
                      xs
                      wid="auto"
                    />
                  </Row>
                  {isLocked && (
                    <Text c2 bold col={theme.SECONDARY}>
                      Locked — this option already has {engagement}
                    </Text>
                  )}
                </Col>
              );
            })}
            {!atMax && (
              <Button
                text="+ Add Prediction"
                onClick={() =>
                  setEditTemplates([
                    ...editTemplates,
                    { home: '', away: '', key: Math.random().toString(36) }
                  ])
                }
                sm
                outlined
                wid="auto"
              />
            )}
          </Col>
        ) : (
          <>
            {(() => {
              const currentPredictions =
                selectedPredictionType === 0 ? predictions.half_time : predictions.full_time;

              if (!currentPredictions || currentPredictions.length === 0) {
                return (
                  <Col center pad="40px 0">
                    <Text b2 col={theme.GREY_60}>
                      No {selectedPredictionType === 0 ? 'half time' : 'full time'} predictions
                      found
                    </Text>
                  </Col>
                );
              }

              return (
                <Col noFlex wid="100%">
                  {currentPredictions.map((prediction) => (
                    <PredictionRow key={prediction._id}>
                      <Row centerAll gap="8px" wid="50%">
                        <Col centerAll gap="4px">
                          <Avatar src={game.home?.avatar} size="small">
                            {game.home?.name?.[0]}
                          </Avatar>
                          <Text c2 center>
                            {game.home?.name || 'TBD'}
                          </Text>
                          <Text
                            b1
                            bold
                            col={
                              parseInt(prediction.predictedScore?.home) >
                              parseInt(prediction.predictedScore?.away)
                                ? '#4CAF50'
                                : theme.GREY_80
                            }
                          >
                            {prediction.predictedScore?.home ?? '-'}
                          </Text>
                        </Col>

                        <Text b1 col={theme.GREY_60}>
                          -
                        </Text>

                        <Col centerAll gap="4px">
                          <Avatar src={game.away?.avatar} size="small">
                            {game.away?.name?.[0]}
                          </Avatar>
                          <Text c2 center>
                            {game.away?.name || 'TBD'}
                          </Text>
                          <Text
                            b1
                            bold
                            col={
                              parseInt(prediction.predictedScore?.away) >
                              parseInt(prediction.predictedScore?.home)
                                ? '#4CAF50'
                                : theme.GREY_80
                            }
                          >
                            {prediction.predictedScore?.away ?? '-'}
                          </Text>
                        </Col>
                      </Row>

                      <Row centerAll gap="16px" wid="30%">
                        <Col center>
                          <Clickable
                            onClick={() =>
                              onViewVotes(prediction._id, prediction.votes?.length || 0)
                            }
                          >
                            <Text h4 bold>
                              {prediction.votes?.length || 0}
                            </Text>
                            <Text c2 col={theme.GREY_60}>
                              Votes
                            </Text>
                          </Clickable>
                        </Col>
                        <Col center>
                          <Clickable
                            onClick={() => {
                              onViewComments(prediction._id, prediction.topLevelCommentsCount || 0);
                            }}
                          >
                            <Text h4 bold>
                              {/* Top-level only, to match the comments modal —
                                  commentsCount also counts replies. */}
                              {prediction.topLevelCommentsCount || 0}
                            </Text>
                            <Text c2 col={theme.GREY_60}>
                              Comments
                            </Text>
                          </Clickable>
                        </Col>
                      </Row>

                      <Row end centerAll gap="8px" wid="20%">
                        <Col end>
                          <Text b2 bold col={theme.SECONDARY}>
                            {prediction.points || 0} pts
                          </Text>
                        </Col>
                      </Row>
                    </PredictionRow>
                  ))}
                </Col>
              );
            })()}
          </>
        )}
      </DetailSection>
    </>
  );
};

export default PredictionsSection;
